import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
import dialerStoreData from "Stores/DialerData";
import DialerServiceData from "Stores/DialerServiceStore";
import TextArea from "Components/common/TextArea";
import Button from "Components/common/Button";
import CallsUtils from "Utils/CallsUtils";
import { mapAiNotes } from "Utils/AiNotesUtils";

const CallLogPopup = observer(props => {
  const { isOpen, onClose, onLogCall, callSid = "" } = props || {};

  const [callOutcome, setCallOutcome] = useState(null);
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState("");
  const [loader, setLoader] = useState(false);
  useEffect(() => {
    if (isOpen) {
      setCallOutcome(null);
      setNotes(DialerServiceData.dialerCallNotes || "");
      setErr("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const outcomeOptions = CallsUtils.getLogOutcomeDropdown(
    dialerStoreData?.stageList,
    () => {}
  );

  const handleOutcomeSelect = option => {
    err && setErr("");
    setCallOutcome(option);
  };

  const handleLogCall = async () => {
    if (!callOutcome?.id) {
      setErr("Please select a call outcome");
      return;
    }
    setLoader(true);
    await onLogCall({
      callDispositionId: callOutcome.id,
      callOutcome: callOutcome.key,
      notes,
      callSid
    });
    setLoader(false);
  };

  const logBtnObj = {
    id: "callLogSubmitBtn",
    name: "callLogSubmitBtn",
    btnText: "Log Call",
    icon: false,
    iconName: "",
    type: "button",
    className: `logCallBtn ${loader ? "loading" : ""}`,
    loader,
    click: handleLogCall
  };

  const aiData = DialerServiceData.callLogAiNotes
    ? mapAiNotes(DialerServiceData.callLogAiNotes)
    : null;
  const aiLoading = DialerServiceData.callLogAiNotesLoading;
  const visibleKeyPoints = aiData?.keyPoints?.slice(0, 4) || [];
  const visibleObjections = aiData?.objections?.slice(0, 2) || [];
  const firstNextStep = aiData?.nextSteps?.[0] || "";

  return (
    <>
      <div className="callLogPopup">
        <div className="callLogPopupCard animated customzoomIn">

          {/* Header */}
          <div className="callLogPopupHeader">
            <div className="callLogPopupTitleSection">
              <div className="callLogPopupTitleRow">
                <span className="callLogPopupTitle">Log your call</span>
                <div className="callLogAiNotesBadge">
                  <i className="material-icons callLogAiNotesBadgeIcon">star</i>
                  <span className="callLogAiNotesBadgeText">AI Notes</span>
                </div>
              </div>
              <span className="callLogPopupSubTitle">
                AI-generated notes from your call
              </span>
            </div>
            <i className="material-icons callLogPopupClose" onClick={onClose}>
              close
            </i>
          </div>

          {/* Two-column body */}
          <div className="callLogPopupBody">

            {/* LEFT — AI notes */}
            <div className="callLogLeftCol">

              {aiLoading && (
                <div className="callLogAiSection callLogAiSection--loading">
                  <div className="callLogAiSkeleton callLogAiSkeleton--summary" />
                  <div className="callLogAiSkeleton callLogAiSkeleton--line" />
                  <div className="callLogAiSkeleton callLogAiSkeleton--line" />
                </div>
              )}

              {!aiLoading && aiData && (
                <>
                  {aiData.summary && (
                    <div className="callLogAiSection">
                      <div className="callLogAiBadge">
                        <span className="callLogAiBadgeText">AI Summary</span>
                      </div>
                      <p className="callLogAiSummary">{aiData.summary}</p>
                    </div>
                  )}

                  {visibleKeyPoints.length > 0 && (
                    <div className="callLogKeyPoints">
                      <p className="callLogSectionLabel">Key Points</p>
                      <div className="callLogKeyPointsList">
                        {visibleKeyPoints.map((point, idx) => (
                          <div key={idx} className="callLogKeyPointItem">
                            <span
                              className={`callLogKeyPointTag tag-${point.cat
                                .toLowerCase()
                                .replace(" ", "-")}`}
                            >
                              {point.cat}
                            </span>
                            <span className="callLogKeyPointText">{point.text}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {visibleObjections.length > 0 && (
                    <div className="callLogObjections">
                      <p className="callLogSectionLabel">Objections</p>
                      <div className="callLogObjectionsList">
                        {visibleObjections.map((obj, idx) => (
                          <div key={idx} className="callLogObjectionItem">
                            <span className="callLogObjectionDot" />
                            <div>
                              <div className="callLogObjectionLabel">{obj.label}</div>
                              <div className="callLogObjectionDesc">{obj.desc}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {firstNextStep && (
                    <div className="callLogNextStep">
                      <p className="callLogSectionLabel">Suggested Next Step</p>
                      <div className="callLogNextStepCard">
                            <div>
                          <div className="callLogNextStepTitle">Suggested action</div>
                          <div className="callLogNextStepDesc">{firstNextStep}</div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              <div className="callLogAdditionalNotes">
                <TextArea
                  id="callLogNotes"
                  labelText="Additional Notes"
                  value={notes}
                  onChangeEvent={e => setNotes(e.currentTarget.value)}
                />
              </div>
            </div>

            {/* RIGHT — outcome + CTA */}
            <div className="callLogRightCol">
              <div className="callLogOutcomeSection">
                <p className="callLogSectionLabel">Call Outcome</p>
                <div className="callLogOutcomeOptions">
                  {outcomeOptions.map(option => (
                    <button
                      key={option.id}
                      className={`callLogOutcomeOption${callOutcome?.id === option.id ? " selected" : ""}`}
                      onClick={() => handleOutcomeSelect(option)}
                    >
                      <span className="callLogOutcomeRadio" />
                      {option.value}
                    </button>
                  ))}
                </div>
                {err && <span className="errMsgText">{err}</span>}
              </div>

              <div className="callLogRightColFooter">
                <div className="callLogAutoSaved">
                  <span className="callLogAutoSavedDot" />
                  Auto-saved to timeline
                </div>
                <div className="callLogCtaButtons">
                  <Button {...logBtnObj} />
                  <button className="callLogCancelBtn" onClick={onClose}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="callLogPopupOverlay" />
    </>
  );
});

export default CallLogPopup;
export { CallLogPopup };
